/**
 * /equipment/cost-postings/{id} — Detail of a single equipment cost
 * posting batch. Shows totals + per-line breakdown + link to underlying
 * journal + reverse action (with reason dialog).
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UndoIcon from "@mui/icons-material/Undo";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  equipmentCostPostingDetailQueryOptions,
  equipmentItemsQueryOptions,
  equipmentPostingKeys,
} from "@/lib/queries";
import type {
  EquipmentCostPosting,
  EquipmentItem,
} from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute(
  "/_auth/equipment/cost-postings/$postingId",
)({
  component: PostingDetailPage,
});

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
}

function PostingDetailPage() {
  const { postingId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const detailQ = useQuery(equipmentCostPostingDetailQueryOptions(postingId));
  const equipmentQ = useQuery(equipmentItemsQueryOptions({ active_only: false }));
  const equipmentMap = new Map<string, EquipmentItem>();
  for (const e of equipmentQ.data ?? []) equipmentMap.set(e.id, e);

  const [reverseOpen, setReverseOpen] = useState(false);
  const [reason, setReason] = useState("");

  const reverseMut = useMutation({
    mutationFn: () =>
      api<EquipmentCostPosting>(
        `/api/construction/equipment/cost-postings/${postingId}/reverse`,
        { method: "POST", body: { reason } },
      ),
    onSuccess: (rev) => {
      toast.success("Posting reversed");
      qc.invalidateQueries({ queryKey: equipmentPostingKeys.all });
      setReverseOpen(false);
      navigate({
        to: "/equipment/cost-postings/$postingId",
        params: { postingId: rev.id },
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (detailQ.isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  const posting = detailQ.data;
  if (!posting) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Posting not found.</Alert>
      </Box>
    );
  }

  const isReversed = posting.status === "reversed";

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/equipment/cost-postings" })}
          >
            Postings
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Posting · {posting.posting_date}
          </Typography>
          <Chip
            size="small"
            label={posting.status}
            color={posting.status === "posted" ? "success" : "warning"}
            variant="outlined"
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpHint
            docPath="equipment-costing/posting-to-job-cost.md"
            label="Posting help"
          />
          {!isReversed && !posting.reverses_posting_id && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<UndoIcon />}
              onClick={() => setReverseOpen(true)}
            >
              Reverse
            </Button>
          )}
        </Stack>
      </Stack>

      {posting.reverses_posting_id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a reversal of posting{" "}
          <Link
            to="/equipment/cost-postings/$postingId"
            params={{ postingId: posting.reverses_posting_id }}
          >
            {posting.reverses_posting_id.slice(0, 8)}…
          </Link>
          .
        </Alert>
      )}
      {posting.reversed_by_posting_id && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Reversed by{" "}
          <Link
            to="/equipment/cost-postings/$postingId"
            params={{ postingId: posting.reversed_by_posting_id }}
          >
            {posting.reversed_by_posting_id.slice(0, 8)}…
          </Link>
          .
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}
        >
          <Field label="Lines" value={posting.line_count} />
          <Field label="Ownership" value={fmtMoney(posting.total_ownership)} />
          <Field label="Operating" value={fmtMoney(posting.total_operating)} />
          <Field label="Transport" value={fmtMoney(posting.total_transport)} />
          <Field
            label="Total"
            value={
              <b>{fmtMoney(posting.total_charge_out)}</b>
            }
          />
          <Field
            label="Journal"
            value={
              <Link
                to="/journals/$journalId"
                params={{ journalId: posting.journal_id }}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                {posting.journal_id.slice(0, 8)}…
              </Link>
            }
          />
          <Field
            label="Created"
            value={new Date(posting.created_at).toLocaleString()}
          />
          {posting.notes && <Field label="Notes" value={posting.notes} />}
        </Box>
      </Paper>

      <Paper variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Lines</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equipment</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Cost code</TableCell>
              <TableCell align="right">Ownership</TableCell>
              <TableCell align="right">Operating</TableCell>
              <TableCell align="right">Transport</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posting.lines.map((ln) => {
              const eq = equipmentMap.get(ln.equipment_id);
              return (
                <TableRow key={ln.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {eq ? eq.code : ln.equipment_id.slice(0, 8) + "…"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    {ln.project_id ? ln.project_id.slice(0, 8) + "…" : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    {ln.cost_code_id
                      ? ln.cost_code_id.slice(0, 8) + "…"
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {fmtMoney(ln.ownership_amount)}
                  </TableCell>
                  <TableCell align="right">
                    {fmtMoney(ln.operating_amount)}
                  </TableCell>
                  <TableCell align="right">
                    {fmtMoney(ln.transport_amount)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {fmtMoney(ln.total_amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Reverse dialog */}
      <Dialog
        open={reverseOpen}
        onClose={() => setReverseOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reverse posting</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <Alert severity="warning">
            Reversing creates a counter-journal that mirror-flips DR/CR signs
            and frees the original time entries to be re-posted.
          </Alert>
          <TextField
            label="Reason (required)"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {reverseMut.error && (
            <Alert severity="error">{String(reverseMut.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reason.trim() || reverseMut.isPending}
            onClick={() => reverseMut.mutate()}
          >
            {reverseMut.isPending ? "Reversing…" : "Reverse posting"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value ?? "—"}</Typography>
    </Box>
  );
}
